import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AddDistributorModalComponent } from './add-distributor-modal.component';
import { UserStatusEnum, KycStatusEnum, CategoryTypeEnum } from './add-distributor-modal.component';
import { ToastService } from '../toast.service';
import { BankDetailsModalComponent } from './bank-details-modal.component';
import { LinkedRetailersModalComponent } from './linked-retailers-modal.component';
import { DocumentModalComponent } from './document-modal.component';
import { DebounceService } from '../debounce.service';

interface Distributor {
  id: string;
  distributor_name: string;
  email: string;
  phone_number: string;
  status: string;
  // Add other fields as needed
  created_at: string;
  retailer_count?: number;
  total_revenue?: number;
  is_admin_approved: boolean;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, SidebarComponent, AddDistributorModalComponent, BankDetailsModalComponent, LinkedRetailersModalComponent, DocumentModalComponent],
  templateUrl: './distributors.component.html',
  styleUrls: ['./distributors.component.scss']
})
export class DistributorsComponent implements OnInit {
  @ViewChild(AddDistributorModalComponent) addModal?: AddDistributorModalComponent;

  distributors: Distributor[] = [];
  pagination: Pagination = {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  };
  searchTerm = '';
  loading = false;
  errorMsg = '';
  showAddModal = false;
  userType = '';
  editingDistributor: Distributor | null = null;
  editingDistributorId: string | null = null;
  selectedDistributorData: any = null;

  showBankModal = false;
  selectedDistributorForBank: any = null;

  showRetailersModal = false;
  selectedDistributorForRetailers: any = null;

  showDocumentModal = false;
  selectedDistributorForDocuments: any = null;

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private debounceService: DebounceService
  ) { }

  ngOnInit() {
    this.loadUserType();
    this.fetchDistributors();
  }

  loadUserType() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.userType = user.user_type?.toLowerCase() || '';
      } catch { }
    }
  }

  fetchDistributors(page: number = 1) {
    this.loading = true;
    this.errorMsg = '';

    // Determine API endpoint based on user type
    let baseUrl = '';
    if (this.userType === 'admin' || this.userType === 'super_admin') {
      baseUrl = `${environment.apiBaseUrl}/admin/distributors`;
    } else if (this.userType === 'staff') {
      baseUrl = `${environment.apiBaseUrl}/staff/distributors`;
    } else {
      // Fallback to admin endpoint
      baseUrl = `${environment.apiBaseUrl}/admin/distributors`;
    }

    let url = `${baseUrl}?page=${page}&limit=${this.pagination.limit}`;
    if (this.searchTerm && this.searchTerm.trim().length > 0) {
      url += `&search=${encodeURIComponent(this.searchTerm.trim())}`;
    }

    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.statusCode === 200 && res.data) {
          this.distributors = res.data.distributors;

          const pg = res.data.pagination;

          this.pagination = {
            ...pg,
            hasNext: pg.page < pg.totalPages,
            hasPrev: pg.page > 1
          };
        } else {
          this.errorMsg = res.message || 'Failed to fetch distributors';
          this.toastService.error(res.message || 'Failed to fetch distributors');
          this.distributors = [];
        }
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg = err?.error?.message || 'Failed to fetch distributors';
        this.toastService.error(err?.error?.message || 'Failed to fetch distributors');
        this.distributors = [];
      }
    });
  }

  onSearch(term: string) {
    this.searchTerm = term;
    this.debounceService.debounce('distributors-search', () => this.fetchDistributors(1));
  }

  goToPage(page: number) {
    if (page < 1 || page > this.pagination.totalPages) return;
    this.fetchDistributors(page);
  }

  editDistributor(distributor: Distributor) {
    const apiUrl = `${environment.apiBaseUrl}/admin/distributor/${distributor.id}`;

    this.http.get<any>(apiUrl).subscribe({
      next: (res) => {
        if (res.statusCode === 200 && res.data) {

          this.editingDistributorId = distributor.id;

          this.selectedDistributorData = {
            distributor_code: res.data.distributor_code || '',
            distributor_name: res.data.distributor_name || '',
            status: res.data.status || UserStatusEnum.ACTIVE,
            email: res.data.email || '',
            phone_number: res.data.phone_number || '',
            alt_phone_number: res.data.alt_phone_number || null,
            gst: res.data.gst || '',
            pan: res.data.pan || '',
            distributor_agency: res.data.distributor_agency || '',
            address_line1: res.data.address_line1 || '',
            address_line2: res.data.address_line2 || '',
            pincode: res.data.pincode || '',
            state: res.data.state || '',
            city: res.data.city || '',
            kyc_status: res.data.kyc_status || KycStatusEnum.PENDING,
            category: res.data.category || CategoryTypeEnum.BASIC
          };

          this.showAddModal = true;

        } else {
          this.toastService.error(res.message || 'Failed to fetch distributor');
        }
      },
      error: (err) => {
        this.toastService.error(err?.error?.message || 'Failed to fetch distributor');
      }
    });
  }

  deleteDistributor(distributor: Distributor) {
    // Implement delete logic
  }

  openAddModal() {
    if (this.userType === 'admin' || this.userType === 'super_admin' || this.userType === 'staff') {

      this.editingDistributorId = null;

      this.selectedDistributorData = {
        distributor_code: '',
        distributor_name: '',
        status: UserStatusEnum.ACTIVE,
        email: '',
        phone_number: '',
        alt_phone_number: null,
        gst: '',
        pan: '',
        distributor_agency: '',
        address_line1: '',
        address_line2: '',
        pincode: '',
        state: '',
        city: '',
        kyc_status: KycStatusEnum.PENDING,
        category: CategoryTypeEnum.BASIC
      };

      this.showAddModal = true;
    }
  }

  closeAddModal() {
    this.showAddModal = false;
    this.editingDistributorId = null;
    this.selectedDistributorData = null;
  }

  handleAddDistributor(payload: any) {
    // Allow admin, super_admin, and staff to create or edit distributors
    if (this.userType !== 'admin' && this.userType !== 'super_admin' && this.userType !== 'staff') {
      this.toastService.error('You do not have permission to add distributors.');
      return;
    }

    // If editing, call update endpoint, else create
    const isEdit = !!this.editingDistributorId;
    const apiUrl = isEdit
      ? `${environment.apiBaseUrl}/admin/update-distributor/${this.editingDistributorId}`
      : `${environment.apiBaseUrl}/admin/create-distributor`;
    const httpCall = isEdit
      ? this.http.patch<any>(apiUrl, payload)
      : this.http.post<any>(apiUrl, payload);

    httpCall.subscribe({
      next: (res) => {
        if (res.statusCode === 200 || res.statusCode === 201) {
          this.toastService.success(res.message || (isEdit ? 'Distributor updated successfully!' : 'Distributor added successfully!'));
          this.closeAddModal();
          this.fetchDistributors(1);
        } else {
          this.toastService.error(res.message || (isEdit ? 'Failed to update distributor' : 'Failed to add distributor'));
          if (this.addModal) {
            this.addModal.resetSubmitting();
          }
        }
      },
      error: (err) => {
        this.toastService.error(err?.error?.message || (isEdit ? 'Failed to update distributor' : 'Failed to add distributor'));
        if (this.addModal) {
          this.addModal.resetSubmitting();
        }
      }
    });
  }

  getDistributorStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'status-success';
      case 'inactive':
        return 'status-failed';
      default:
        return '';
    }
  }

  // --- Bank Details Modal logic ---
  openBankModal(distributor: any) {
    this.selectedDistributorForBank = distributor;
    this.showBankModal = true;
  }

  closeBankModal() {
    this.showBankModal = false;
    this.selectedDistributorForBank = null;
  }

  onBankDetailsSaved(updatedBankArray: any[]) {
    if (this.selectedDistributorForBank) {
      this.selectedDistributorForBank.bank_details = updatedBankArray;
    }
  }

  // --- Retailers Modal logic ---
  openRetailersModal(distributor: any) {
    this.selectedDistributorForRetailers = distributor;
    this.showRetailersModal = true;
  }

  closeRetailersModal() {
    this.showRetailersModal = false;
    this.selectedDistributorForRetailers = null;
  }

  // --- Document Modal logic ---
  openDocumentModal(distributor: any) {
    this.selectedDistributorForDocuments = distributor;
    this.showDocumentModal = true;
  }

  closeDocumentModal() {
    this.showDocumentModal = false;
    this.selectedDistributorForDocuments = null;
  }

  onDocumentsSaved() {
    // Optionally refresh the list or just show toast (which is done in modal)
    // this.fetchDistributors(this.pagination.page);
  }
}
